import styled from "@emotion/styled";
import { LoadingButton } from "@mui/lab";
import {
  alpha,
  Box,
  ButtonBase,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { DefaultUrlWeb } from "config/Configuration";
import { useEffect, useState } from "react";
import SubscriptionPlanModal from "./SubscriptionPlanModal";
import SubscriptionSuccessfulModal from "./SubscriptionSuccessfulModal";
import { VALUE_INVESTMENT_FUND } from "pages/Wallet/constants";
import { Left } from "assets/Icon";
import { useNavigate } from "react-router-dom";
import useIsMobileView from "hooks/useIsMobileView";
import { actionButtonBg } from "utils/helpers/actionButtonBg";

const numberDays = [
  { days: 60, interestRates: 15, active: true },
  { days: 90, interestRates: 20, active: false },
  { days: 120, interestRates: 25, active: false },
  { days: 360, interestRates: 30, active: false },
];

function BoxFixedYearlyProfit({ investmentFund, setInvestmentFund, investmentDetail }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();

  const [activeItem, setActiveItem] = useState(null)

  const [openModalSubscriptionPlan, setOpenModalSubscriptionPlan] = useState(false);
  const [openModalSubscriptionSuccessful, setOpenModalSubscriptionSuccessful] = useState(false);

  const handleClickSelectDays = (item) => {
    setActiveItem(item?.objInvestmentFixed)
  };

  const handleShowView = async (data) => {
    navigate(`/investment-fund/view-fixed-yearly-profit`);
  };

  useEffect(() => {
    setActiveItem(investmentDetail?.[0]?.objInvestmentFixed)
  }, [investmentDetail])

  return (
    <>
      <InvestmentCard>
        <Grid container p={1}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" my={2} alignItems={"center"}>
              <img
                src={DefaultUrlWeb + activeItem?.baseCoinImageUrl}
                alt={activeItem?.baseCoinName}
                width={25}
                height={25}
              />

              <Typography
                variant={isMobileView ? "h5" : "h3"}
                pl={0.5}
                color="text.main"
              >
                {activeItem?.baseCoinName}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="center" mt={2} alignItems={"center"}>
              <Typography
                variant={isMobileView ? "h5" : "h3"}
                pl={0.5}
                color="text.main"
              >
                {activeItem?.portfolioName}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="center" mt={1} mb={2} alignItems={"center"}>
              <Typography
                variant={isMobileView ? "h6" : "h5"}
                pl={0.5}
                color="text.secondary"
              >
                {activeItem?.name}
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {investmentDetail?.map((item) => (
                <Grid item xs={6}>
                  <BoxStyle
                    component={ButtonBase}
                    onClick={() => handleClickSelectDays(item)}
                    active={item?.objInvestmentFixed?.id === activeItem?.id}
                  >
                    <Typography
                      variant={isMobileView ? "h5" : "h3"}
                      fontWeight={500}
                      color="secondary.main"
                    >
                      {item?.objInvestmentFixed?.id === activeItem?.id && (
                        <BoxIcon>
                          <ion-icon name="checkmark"></ion-icon>
                        </BoxIcon>
                      )}
                      {item?.objInvestmentFixed?.dayInvestment}
                      <Typography
                        fontWeight={300}
                        variant={isMobileView ? "h5" : "h3"}
                        color="secondary.main"
                      >
                        Days
                      </Typography>
                    </Typography>
                  </BoxStyle>
                </Grid>
              ))}
            </Grid>


            <Box display="flex" alignItems="center" gap={3} justifyContent={"center"}>
              <Box display="flex" alignItems="center" flexDirection="column" my={3}>
                <Typography
                  variant={isMobileView ? "h6" : "h4"}
                  fontWeight={300}
                  color="text.main"
                >
                  Fixed Profit
                </Typography>

                <Typography
                  fontWeight={500}
                  variant={isMobileView ? "h4" : "h2"}
                  color="secondary.main"
                  pt={2}
                >
                  {activeItem?.customerFixedProfitPercent}%
                </Typography>
              </Box>
            </Box>

            <Grid item xs={12} mt={3} mb={2}>
              <LoadingButton
                color={actionButtonBg(theme)}
                fullWidth
                variant="contained"
                onClick={() => setOpenModalSubscriptionPlan(true)}
              >
                Subscribe
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </InvestmentCard>

      {openModalSubscriptionPlan && (
        <SubscriptionPlanModal
          onClose={() => setOpenModalSubscriptionPlan(false)}
          open={openModalSubscriptionPlan}
          setOpenModalSubscriptionSuccessful={setOpenModalSubscriptionSuccessful}
          activeItem={activeItem}
          investmentDetail={investmentDetail}
        />
      )}
      {openModalSubscriptionSuccessful && (
        <SubscriptionSuccessfulModal
          variant="success"
          handleShowView={handleShowView}
          onClose={() => setOpenModalSubscriptionSuccessful(false)}
          open={openModalSubscriptionSuccessful}
        />
      )}
    </>
  );
}

const InvestmentCard = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: "20px",
  background: theme.palette.background.primaryGradient,
  padding: theme.spacing(2),
  border: "1px solid transparent",
  transition: "all 0.1s",

  "&:hover": {
    border: `1px solid ${alpha(
      theme.palette.secondary.main,
      0.3
    )}`,
    background: `linear-gradient(0deg, ${alpha(
      theme.palette.secondary.main,
      0.1
    )} 0%, rgba(0,38,78,0) 100%)`,
  },
}));

const BoxIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  color: theme.palette.primary.main,
  right: 0,
  top: 0,
}));

const BoxStyle = styled(Box)(({ theme, active }) => ({
  background: active
    ? theme.palette.background.activeCart
    : theme.palette.background.primaryGradient,
  border: `1px solid ${theme.palette.modules.border}`,
  position: "relative",
  width: "100%",
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderRadius: "5px",
  "&:before": active && {
    content: "''",
    position: "absolute",
    borderTop: "60px solid rgb(111 203 239)",
    borderLeft: "60px solid transparent",
    right: 0,
    top: 0,
    transition: "all .4s ease-in-out",
    borderRadius: "0px 5px 0 0",
  },
}));

export default BoxFixedYearlyProfit;
